import React, {Component} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {ComposerSectionRightActions} from './composerActionRightActions';
import {ComposerSection} from './composerSection';
import {ComposerSectionAddButton} from './composerSectionAddButton';
import {ComposerSectionButton} from './composerSectionButton';
import {ComposerSectionContent} from './composerSectionContent';
import {ComposerSectionLeftActions} from './composerSectionLeftActions';
import {ComposerSectionInlineToggleButton, ComposerSectionToggleButton} from './composerSectionToggleButton';

/*
 * Props.
 */

interface ComposerSectionEditorCoordinatorProps {
  /** Whether to include this section, or render a button to add it. */
  isIncluded: boolean;
  onAdd: Handler;
  onRemove: Handler;
  renderAddLabel: Renderer;
  renderRemoveLabel: Renderer;

  /** Whether this section can be collapsed. */
  canHide?: boolean;
  /** Whether this section is expanded. */
  isVisible?: boolean;
  onToggleIsVisible?: Handler;
  renderShowLabel?: Renderer;
  renderHideLabel?: Renderer;
  /** Render the static un-editable content of this section. */
  renderStatic: Renderer;

  /** Whether this section is displaying statically, or being edited. */
  isAlwaysEditing?: boolean;
  isEditing?: boolean;
  onEdit: Handler;
  onRevert: Handler;
  /** Render the editable content of this section. */
  renderEditor: Renderer;

  hasHover?: boolean;
  hasFocus?: boolean;
  /** Render extra actions between the Edit and X buttons. (optional) */
  renderExtraRightActions?: Renderer;
}

/*
 * Style.
 */

const buttonWrapperStyle = css`
  align-self: flex-start;
  margin: 8px 10px 8px 10px;

  &:last-child {
    margin-bottom: 10px;
  }
`;

const StyledAddButtonWrapper = styled.div`
  ${buttonWrapperStyle};
  margin-left: 11px;

  & + & {
    margin-top: -8px;
  }
`;

const StyledToggleButtonWrapper = styled.div`
  ${buttonWrapperStyle};

  & + &,
  ${StyledAddButtonWrapper} + & {
    margin-top: 0;
  }
`;

/*
 * Component.
 */

export class ComposerSectionEditorCoordinator extends Component<ComposerSectionEditorCoordinatorProps> {
  render() {
    // If this is not included in the message, show the button to add it.
    const {isIncluded, onAdd, renderAddLabel} = this.props;
    if (!isIncluded) {
      return (
        <StyledAddButtonWrapper>
          <ComposerSectionAddButton onClick={onAdd}>{renderAddLabel()}</ComposerSectionAddButton>
        </StyledAddButtonWrapper>
      );
    }

    // If this is collapsed, show the button expand it.
    const {canHide, isVisible, onToggleIsVisible, renderShowLabel} = this.props;
    if (canHide && !isVisible && renderShowLabel) {
      return (
        <StyledToggleButtonWrapper>
          <TooltipCoordinator render={renderShowLabel}>
            <ComposerSectionToggleButton
              label={renderShowLabel()}
              isExpanded={false}
              onClick={onToggleIsVisible}
            />
          </TooltipCoordinator>
        </StyledToggleButtonWrapper>
      );
    }

    const {isAlwaysEditing, onRemove, renderRemoveLabel, hasHover, hasFocus, renderExtraRightActions} =
      this.props;
    const isEditing = isAlwaysEditing || this.props.isEditing;
    return (
      <ComposerSection
        hasHover={hasHover || (isEditing && hasFocus)}
        hasFocus={isEditing && hasFocus}
        hoverOutline={isAlwaysEditing ? hasFocus : isEditing}
      >
        <ComposerSectionContent>
          {isEditing ? this.props.renderEditor() : this.props.renderStatic()}
        </ComposerSectionContent>

        {this.maybeRenderLeftActions()}

        <ComposerSectionRightActions>
          {this.maybeRenderEditingActions()}
          {renderExtraRightActions && renderExtraRightActions()}
          <TooltipCoordinator render={renderRemoveLabel}>
            <ComposerSectionButton onClick={onRemove}>
              <Icon name="exitTiny" />
              <VisuallyHidden>{renderRemoveLabel()}</VisuallyHidden>
            </ComposerSectionButton>
          </TooltipCoordinator>
        </ComposerSectionRightActions>
      </ComposerSection>
    );
  }

  private maybeRenderLeftActions() {
    const {canHide, renderHideLabel} = this.props;
    if (!canHide || !renderHideLabel) {
      return null;
    }

    const {onToggleIsVisible} = this.props;
    return (
      <ComposerSectionLeftActions>
        <TooltipCoordinator render={renderHideLabel}>
          <ComposerSectionInlineToggleButton
            label={renderHideLabel()}
            isExpanded
            onClick={onToggleIsVisible}
          />
        </TooltipCoordinator>
      </ComposerSectionLeftActions>
    );
  }

  private maybeRenderEditingActions() {
    const {isAlwaysEditing, isEditing} = this.props;
    if (isAlwaysEditing) {
      return null;
    }

    return isEditing ? this.renderRevert() : this.renderEdit();
  }

  private renderEdit() {
    const {onEdit} = this.props;

    return (
      <ComposerSectionButton onClick={onEdit}>
        <Trans id="composer-section-editor-edit">Edit</Trans>
      </ComposerSectionButton>
    );
  }

  private renderRevert() {
    const {onRevert} = this.props;

    return (
      <ComposerSectionButton onClick={onRevert} color={PaletteColorsEnum.RED}>
        <Trans id="composer-section-editor-revert">Revert</Trans>
      </ComposerSectionButton>
    );
  }
}
